/**
 * CALIDAD — FASE 7.1
 *
 *   /calidad                          Incidencias · No conformidades · Acciones pendientes
 *   /calidad/incidencias/:id          Detalle: contexto, evidencias, línea de tiempo, «Escalar a NC»
 *   /calidad/no-conformidades/:id     El MISMO detalle de NC que en la OT (+ ficha de calidad)
 *   /reportar                         OPERARIO: parte corto desde el móvil
 *
 * «Escalar a NC» crea una NC normal: si la OT estaba cerrada, el servidor
 * la reabre solo (reapertura automática existente). Aquí no hay — ni debe
 * haber — ningún botón de «reabrir OT».
 */
import { useId, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  api,
  type AccionCorrectiva,
  type EstadoIncidencia,
  type GravedadNc,
  type IncidenciaDetalle,
  type NcDetalle,
  type NcVinculable,
  type PaginaIncidencias,
  type PaginaNc,
  type ResultadoEscalar,
} from '../api.ts';
import {
  EstadoIncidenciaEtiqueta,
  etiquetaEstadoIncidencia,
  FormularioIncidencia,
  LineaDeTiempo,
  NivelEtiqueta,
  SelectorResponsable,
  SIGUIENTES_INCIDENCIA,
  TarjetaAccion,
  TarjetaIncidencia,
  useResponsables,
} from '../componentes/calidad.tsx';
import { Cargando, Error as ErrorCaja, EstadoNc, etiquetaEstadoOt, fecha, fechaHora, useDatos, Vacio } from '../componentes/base.tsx';
import { Evidencias, EvidenciasPropias } from '../componentes/evidencias.tsx';
import {
  DetalleNc,
  OrigenNc,
  textoAntiguedadNc,
  textoAutorNc,
  textoContextoNc,
  VisibilidadNc,
} from '../componentes/no-conformidades.tsx';
import { PestanaIndicadores } from './calidad-indicadores.tsx';
import { rutaInterna } from '../componentes/indicadores-mnt.tsx';
import { ExportarInformeCalidad } from '../componentes/exportar-informe-calidad.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';
import { useListaUrl } from '../listaUrl.ts';

type Pestana = 'incidencias' | 'nc' | 'acciones' | 'indicadores';
const TODAS_PESTANAS: readonly { clave: Pestana; texto: string; permiso?: string }[] = [
  { clave: 'incidencias', texto: 'Incidencias' },
  { clave: 'nc', texto: 'No conformidades' },
  { clave: 'acciones', texto: 'Acciones pendientes' },
  //  FASE 7.4: sólo con `calidad:indicadores` (el servidor lo exige igual).
  { clave: 'indicadores', texto: 'Indicadores', permiso: 'calidad:indicadores' },
];

export function PaginaCalidad(): ReactNode {
  const { puede } = useSesion();
  const PESTANAS = TODAS_PESTANAS.filter((p) => p.permiso === undefined || puede(p.permiso));
  const [params, setParams] = useSearchParams();
  const pestana = (PESTANAS.some((p) => p.clave === params.get('tab')) ? params.get('tab') : 'incidencias') as Pestana;
  return (
    <Shell
      titulo="Calidad"
      tabs={
        <nav className="tabs" aria-label="Secciones de calidad" role="tablist">
          {PESTANAS.map((p) => (
            <a
              key={p.clave}
              href={`?tab=${p.clave}`}
              role="tab"
              aria-selected={pestana === p.clave}
              className={pestana === p.clave ? 'activo' : ''}
              onClick={(e) => {
                e.preventDefault();
                setParams({ tab: p.clave });
              }}
            >
              {p.texto}
            </a>
          ))}
        </nav>
      }
    >
      {pestana === 'incidencias' && <PestanaIncidencias />}
      {pestana === 'nc' && <PestanaNc />}
      {pestana === 'acciones' && <PestanaAcciones />}
      {pestana === 'indicadores' && <PestanaIndicadores />}
    </Shell>
  );
}

// ------------------------------------------------------------ filtros (FASE 7.2)

/** Filtros operativos comunes — sólo los útiles en el día a día, nunca un buscador empresarial. */
function useFiltrosCalidad(): {
  idCentro: string; setIdCentro: (v: string) => void;
  idResponsable: string; setIdResponsable: (v: string) => void;
  mias: boolean; setMias: (v: boolean) => void;
  vencidas: boolean; setVencidas: (v: boolean) => void;
  consulta: (q: URLSearchParams) => URLSearchParams;
  deps: readonly unknown[];
} {
  const [idCentro, setIdCentro] = useState('');
  const [idResponsable, setIdResponsable] = useState('');
  const [mias, setMias] = useState(false);
  const [vencidas, setVencidas] = useState(false);
  return {
    idCentro, setIdCentro, idResponsable, setIdResponsable, mias, setMias, vencidas, setVencidas,
    consulta: (q) => {
      if (idCentro !== '') q.set('idCentro', idCentro);
      if (mias) q.set('mias', 'true');
      else if (idResponsable !== '') q.set('idResponsable', idResponsable);
      if (vencidas) q.set('vencidas', 'true');
      return q;
    },
    deps: [idCentro, idResponsable, mias, vencidas],
  };
}

function FiltrosComunes({ f, conVencidas = true }: { f: ReturnType<typeof useFiltrosCalidad>; conVencidas?: boolean }): ReactNode {
  const idBase = useId();
  const centros = useDatos(() => api.get<{ filas: { id: string; descripcion: string; cliente: string }[] }>('/api/calidad/centros'), []);
  const responsables = useResponsables();
  return (
    <>
      <div className="campo" style={{ margin: 0 }}>
        <label htmlFor={`${idBase}-cen`}>Centro</label>
        <select id={`${idBase}-cen`} value={f.idCentro} onChange={(e) => f.setIdCentro(e.target.value)}>
          <option value="">Todos</option>
          {(centros.datos?.filas ?? []).map((c) => (
            <option key={c.id} value={c.id}>{`${c.descripcion} · ${c.cliente}`}</option>
          ))}
        </select>
      </div>
      <div className="campo" style={{ margin: 0 }}>
        <label htmlFor={`${idBase}-resp`}>Responsable</label>
        <SelectorResponsable id={`${idBase}-resp`} valor={f.idResponsable} onChange={f.setIdResponsable} responsables={responsables} vacio="Cualquiera" />
      </div>
      <label className="fila" style={{ gap: 6 }}>
        <input type="checkbox" checked={f.mias} onChange={(e) => f.setMias(e.target.checked)} />
        Mías
      </label>
      {conVencidas && (
        <label className="fila" style={{ gap: 6 }}>
          <input type="checkbox" checked={f.vencidas} onChange={(e) => f.setVencidas(e.target.checked)} />
          Sólo vencidas
        </label>
      )}
    </>
  );
}

// ------------------------------------------------------------ pestaña incidencias

function PestanaIncidencias(): ReactNode {
  const { puede } = useSesion();
  const idBase = useId();
  const filtros = useFiltrosCalidad();
  const [estado, setEstado] = useState('ABIERTAS');
  const [gravedad, setGravedad] = useState('');
  const [creando, setCreando] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const lista = useDatos(() => {
    const q = new URLSearchParams({ porPagina: '100' });
    if (estado !== '') q.set('estado', estado);
    if (gravedad !== '') q.set('gravedad', gravedad);
    return api.get<PaginaIncidencias>(`/api/incidencias?${filtros.consulta(q).toString()}`);
  }, [estado, gravedad, ...filtros.deps]);

  return (
    <>
      <div className="panel">
        <div className="panel-cuerpo">
          <div className="fila filtros-calidad" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="campo" style={{ margin: 0 }}>
              <label htmlFor={`${idBase}-est`}>Estado</label>
              <select id={`${idBase}-est`} value={estado} onChange={(e) => setEstado(e.target.value)}>
                <option value="ABIERTAS">Pendientes (abiertas y en análisis)</option>
                <option value="">Todas</option>
                {(['ABIERTA', 'EN_ANALISIS', 'RESUELTA', 'CERRADA', 'CANCELADA'] as const).map((e) => (
                  <option key={e} value={e}>{etiquetaEstadoIncidencia(e)}</option>
                ))}
              </select>
            </div>
            <div className="campo" style={{ margin: 0 }}>
              <label htmlFor={`${idBase}-niv`}>Nivel</label>
              <select id={`${idBase}-niv`} value={gravedad} onChange={(e) => setGravedad(e.target.value)}>
                <option value="">Todos</option>
                <option value="LEVE">Leve</option>
                <option value="GRAVE">Grave</option>
                <option value="CRITICA">Crítica</option>
              </select>
            </div>
            <FiltrosComunes f={filtros} />
            {puede('incidencias:crear') && !creando && (
              <button type="button" className="principal" onClick={() => { setCreando(true); setMensaje(null); }}>
                Nueva incidencia
              </button>
            )}
          </div>
        </div>
      </div>
      <ExportarInformeCalidad
        tipo="INCIDENCIAS" perspectiva="INTERNA" idCentro={filtros.idCentro} estado={estado} gravedad={gravedad}
        noAplicados={[
          ...(filtros.idResponsable !== '' ? ['responsable'] : []), ...(filtros.mias ? ['«Mías»'] : []), ...(filtros.vencidas ? ['vencidas'] : []),
        ]}
      />
      {mensaje !== null && <div className="nota exito" role="status">{mensaje}</div>}
      {creando && (
        <FormularioIncidencia
          contexto={{}}
          cancelar={() => setCreando(false)}
          alCrear={(r) => {
            setCreando(false);
            setMensaje(`Incidencia ${r.codigo} registrada.`);
            lista.recargar();
          }}
        />
      )}
      <div className="panel">
        <div className="panel-cuerpo">
          {lista.cargando && lista.datos === null && <Cargando texto="Cargando incidencias…" />}
          {lista.error !== null && <ErrorCaja error={lista.error} reintentar={lista.recargar} />}
          {lista.datos !== null &&
            (lista.datos.filas.length === 0 ? (
              <Vacio titulo="No hay incidencias con estos filtros" />
            ) : (
              <>
                <p className="pequena tenue">{lista.datos.total} incidencia(s)</p>
                <ul className="lista-nc">
                  {lista.datos.filas.map((f) => <TarjetaIncidencia key={f.id} f={f} />)}
                </ul>
              </>
            ))}
        </div>
      </div>
    </>
  );
}

// ------------------------------------------------------------ pestaña NC

/**
 * F11.1 — abre en las ACTIVAS (abiertas y en tratamiento): lo que requiere
 * atención. Cerradas, anuladas y todas siguen a un filtro de distancia.
 */
/** F11.4 — filtros del listado a los que llevan los Indicadores (drill-down), con su texto. */
const DRILL_NC = ['sinResponsable', 'antiguedad', 'idUbicacion', 'reabiertas', 'sinAtender', 'origen', 'desde', 'hasta'] as const;
/** Cómo se llama en pantalla cada filtro del drill-down que el Excel no aplica. */
const NOMBRE_DRILL_NC: Readonly<Record<Exclude<(typeof DRILL_NC)[number], 'desde' | 'hasta'>, string>> = {
  sinResponsable: 'sin responsable', antiguedad: 'antigüedad', idUbicacion: 'ubicación', reabiertas: 'reabiertas',
  sinAtender: 'sin atender', origen: 'origen',
};
const TEXTO_ANTIGUEDAD: Record<string, string> = { '0-2': '0–2 días', '3-7': '3–7 días', '8-14': '8–14 días', '15-30': '15–30 días', '31+': 'más de 30 días' };
const TEXTO_ORIGEN_DRILL: Record<string, string> = { INTERNA: 'Interna', CLIENTE: 'Cliente', AUDITORIA: 'Auditoría' };

function PestanaNc(): ReactNode {
  const idBase = useId();
  //  F11.4: TODOS los filtros en la URL — un indicador puede traer aquí su filtro exacto, y al volver de
  //  una ficha el listado está como se dejó. `volver` es la URL de los indicadores de los que se vino.
  const url = useListaUrl({
    tab: 'nc', estado: 'ACTIVAS', gravedad: '', idCentro: '', idResponsable: '', mias: '', vencidas: '',
    sinResponsable: '', antiguedad: '', idUbicacion: '', ubicacion: '', reabiertas: '', sinAtender: '', origen: '', desde: '', hasta: '', volver: '',
  });
  const estado = url.valor('estado');
  const gravedad = url.valor('gravedad');
  const filtros: ReturnType<typeof useFiltrosCalidad> = {
    idCentro: url.valor('idCentro'), setIdCentro: (v) => url.fijar('idCentro', v),
    idResponsable: url.valor('idResponsable'), setIdResponsable: (v) => url.fijar('idResponsable', v),
    mias: url.valor('mias') === 'true', setMias: (v) => url.fijar('mias', v ? 'true' : ''),
    vencidas: url.valor('vencidas') === 'true', setVencidas: (v) => url.fijar('vencidas', v ? 'true' : ''),
    consulta: (q) => {
      for (const k of ['idCentro', 'idResponsable', ...DRILL_NC] as const) if (url.valor(k) !== '') q.set(k, url.valor(k));
      if (url.valor('mias') === 'true') q.set('mias', 'true');
      if (url.valor('vencidas') === 'true') q.set('vencidas', 'true');
      return q;
    },
    deps: [],
  };
  const consulta = filtros.consulta(new URLSearchParams({ porPagina: '100', ...(estado === 'TODAS' ? {} : { estado }), ...(gravedad === '' ? {} : { gravedad }) })).toString();
  const lista = useDatos(() => api.get<PaginaNc>(`/api/no-conformidades?${consulta}`), [consulta]);
  const filas = lista.datos?.filas ?? [];
  //  F13.7: sólo una ruta interna (ver rutaInterna).
  const volver = rutaInterna(url.valor('volver'));
  const chips: { clave: string; texto: string; quitar: Partial<Record<'sinResponsable' | 'antiguedad' | 'idUbicacion' | 'ubicacion' | 'reabiertas' | 'sinAtender' | 'origen' | 'desde' | 'hasta', string>> }[] = [];
  if (url.valor('sinResponsable') === 'true') chips.push({ clave: 's', texto: 'Sin responsable', quitar: { sinResponsable: '' } });
  if (url.valor('antiguedad') !== '') chips.push({ clave: 'a', texto: `Abiertas hace ${TEXTO_ANTIGUEDAD[url.valor('antiguedad')] ?? url.valor('antiguedad')}`, quitar: { antiguedad: '' } });
  if (url.valor('idUbicacion') !== '') chips.push({ clave: 'u', texto: `Ubicación: ${url.valor('idUbicacion') === 'SIN' ? 'sin ubicación' : url.valor('ubicacion') || 'elegida'}`, quitar: { idUbicacion: '', ubicacion: '' } });
  if (url.valor('reabiertas') === 'true') chips.push({ clave: 'r', texto: 'Reabiertas alguna vez', quitar: { reabiertas: '' } });
  if (url.valor('sinAtender') === 'true') chips.push({ clave: 'c', texto: 'El cliente espera respuesta', quitar: { sinAtender: '' } });
  if (url.valor('origen') !== '') chips.push({ clave: 'o', texto: `Origen: ${TEXTO_ORIGEN_DRILL[url.valor('origen')] ?? url.valor('origen')}`, quitar: { origen: '' } });
  if (url.valor('desde') !== '' || url.valor('hasta') !== '') {
    chips.push({ clave: 'p', texto: `Registradas del ${url.valor('desde') === '' ? '…' : fecha(url.valor('desde'))} al ${url.valor('hasta') === '' ? '…' : fecha(url.valor('hasta'))}`, quitar: { desde: '', hasta: '' } });
  }
  //  PC-v2.2-C2.3-C: el Excel repite centro, estado y nivel; el periodo del drill-down («registradas del … al …») es el suyo.
  //  C2.3-C-R2: cada filtro activo que el Excel no aplica, por su nombre. El periodo del drill-down SÍ se aplica (mismo
  //  criterio: día de alta en la hora del centro) cuando llega completo; si llega a medias, el Excel usa el periodo por defecto.
  const noAplicados = [
    ...(url.valor('idResponsable') !== '' ? ['responsable'] : []), ...(url.valor('mias') === 'true' ? ['«Mías»'] : []),
    ...(url.valor('vencidas') === 'true' ? ['vencidas'] : []),
    ...(Object.keys(NOMBRE_DRILL_NC) as (keyof typeof NOMBRE_DRILL_NC)[]).filter((k) => url.valor(k) !== '').map((k) => NOMBRE_DRILL_NC[k]),
    ...((url.valor('desde') === '') !== (url.valor('hasta') === '') ? ['periodo incompleto de la vista'] : []),
  ];
  return (
    <>
    <ExportarInformeCalidad
      tipo="NO_CONFORMIDADES" perspectiva="INTERNA" idCentro={url.valor('idCentro')} estado={estado} gravedad={gravedad}
      desdeVista={url.valor('desde')} hastaVista={url.valor('hasta')} noAplicados={noAplicados}
    />
    <div className="panel">
      <div className="panel-cuerpo">
        {volver !== '' && (
          <p style={{ marginTop: 0 }}><Link to={volver} className="boton boton-tactil">← Volver a Indicadores</Link></p>
        )}
        {chips.length > 0 && (
          <div className="fila chips-drill" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 10 }} role="group" aria-label="Filtros desde Indicadores">
            {chips.map((c) => (
              <button key={c.clave} type="button" className="chip-filtro" onClick={() => url.fijarVarios(c.quitar)} aria-label={`Quitar filtro: ${c.texto}`}>
                {c.texto} <span aria-hidden="true">✕</span>
              </button>
            ))}
          </div>
        )}
        <div className="fila filtros-calidad" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-est`}>Estado</label>
            <select id={`${idBase}-est`} value={estado} onChange={(e) => url.fijar('estado', e.target.value)}>
              <option value="ACTIVAS">Activas (abiertas y en tratamiento)</option>
              <option value="ABIERTA">Abierta</option>
              <option value="EN_TRATAMIENTO">En tratamiento</option>
              <option value="CERRADA">Cerrada</option>
              <option value="ANULADA">Anulada</option>
              <option value="TODAS">Todas</option>
            </select>
          </div>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-niv`}>Nivel</label>
            <select id={`${idBase}-niv`} value={gravedad} onChange={(e) => url.fijar('gravedad', e.target.value)}>
              <option value="">Todos</option>
              <option value="LEVE">Leve</option>
              <option value="GRAVE">Grave</option>
              <option value="CRITICA">Crítica</option>
              <option value="PENDIENTE">Pendiente de clasificar</option>
            </select>
          </div>
          <FiltrosComunes f={filtros} />
        </div>
        {lista.cargando && lista.datos === null && <Cargando texto="Cargando no conformidades…" />}
        {lista.error !== null && <ErrorCaja error={lista.error} reintentar={lista.recargar} />}
        {lista.datos !== null &&
          (filas.length === 0 ? (
            <Vacio titulo="No hay no conformidades con estos filtros" />
          ) : (
            <ul className="lista-nc">
              {filas.map((n) => {
                const antiguedad = textoAntiguedadNc(n);
                return (
                  <li key={n.id} className="nc-tarjeta">
                    <Link to={`/calidad/no-conformidades/${n.id}`} className="nc-tarjeta-cabecera" style={{ textDecoration: 'none' }}>
                      <span className="fila" style={{ gap: 8, flexWrap: 'wrap' }}>
                        <span className="mono">{n.codigo}</span>
                        <EstadoNc estado={n.estado} />
                        <NivelEtiqueta nivel={n.gravedad} />
                        {n.vencida === true && <span className="etiqueta error">Vencida</span>}
                        {n.esperaRespuesta === true && <span className="etiqueta aviso">El cliente espera respuesta</span>}
                      </span>
                      <span className="pequena suave">{fechaHora(n.creadoEn)}</span>
                    </Link>
                    <div className="nc-tarjeta-resumen">
                      <p>{n.descripcion}</p>
                      <p className="pequena">
                        {[n.centroNombre, n.ubicacionNombre].filter((x) => x != null && x !== '').join(' · ')}
                        {n.centroNombre != null && ' · '}
                        {textoContextoNc(n)}
                      </p>
                      <div className="fila" style={{ gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                        <OrigenNc origen={n.origen} />
                        {n.visibleCliente !== undefined && <VisibilidadNc visible={n.visibleCliente} />}
                      </div>
                      <p className="pequena tenue">
                        {textoAutorNc(n)}
                        {antiguedad !== null && ` · ${antiguedad}`}
                        {` · ${n.responsableNombre != null ? `Responsable: ${n.responsableNombre}` : 'Sin responsable'}`}
                        {n.fechaObjetivo != null && ` · Objetivo: ${fecha(n.fechaObjetivo)}`}
                        {n.ultimaActividadCliente != null && ` · Actividad del cliente: ${fechaHora(n.ultimaActividadCliente)}`}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          ))}
      </div>
    </div>
    </>
  );
}

// ------------------------------------------------------------ pestaña acciones

/**
 * FASE 7.2 — bandeja operable: se empieza, se completa (con resultado), se
 * adjunta evidencia y se edita (gestión) SIN ir a buscar la NC.
 */
function PestanaAcciones(): ReactNode {
  const idBase = useId();
  const filtros = useFiltrosCalidad();
  const responsables = useResponsables();
  const [estado, setEstado] = useState('ABIERTAS');
  const lista = useDatos(() => {
    const q = new URLSearchParams({ estado });
    return api.get<{ filas: AccionCorrectiva[] }>(`/api/acciones-correctivas?${filtros.consulta(q).toString()}`);
  }, [estado, ...filtros.deps]);
  return (
    <div className="panel">
      <div className="panel-cuerpo">
        <div className="fila filtros-calidad" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-est`}>Estado</label>
            <select id={`${idBase}-est`} value={estado} onChange={(e) => setEstado(e.target.value)}>
              <option value="ABIERTAS">Pendientes y en curso</option>
              <option value="PENDIENTE">Pendiente</option>
              <option value="EN_CURSO">En curso</option>
              <option value="COMPLETADA">Completada</option>
              <option value="CANCELADA">Cancelada</option>
              <option value="TODAS">Todas</option>
            </select>
          </div>
          <FiltrosComunes f={filtros} />
        </div>
        {lista.cargando && lista.datos === null && <Cargando texto="Cargando acciones…" />}
        {lista.error !== null && <ErrorCaja error={lista.error} reintentar={lista.recargar} />}
        {lista.datos !== null &&
          (lista.datos.filas.length === 0 ? (
            <Vacio titulo="No hay acciones correctivas con estos filtros" />
          ) : (
            <ul className="lista-nc">
              {lista.datos.filas.map((a) => (
                <TarjetaAccion
                  key={a.id}
                  accion={a}
                  modo={a.estadoNc === 'ABIERTA' || a.estadoNc === 'EN_TRATAMIENTO' ? 'gestion' : 'lectura'}
                  responsables={responsables}
                  mostrarNc
                  alCambiar={lista.recargar}
                />
              ))}
            </ul>
          ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------ Mis acciones (responsable)

/** FASE 7.2 — el responsable (p.ej. operaria) trabaja SUS acciones: empezar, completar con resultado, foto. */
export function PaginaMisAcciones(): ReactNode {
  const [soloAbiertas, setSoloAbiertas] = useState(true);
  const lista = useDatos(
    () => api.get<{ filas: AccionCorrectiva[] }>(`/api/mis-acciones?estado=${soloAbiertas ? 'ABIERTAS' : 'TODAS'}`),
    [soloAbiertas],
  );
  return (
    <Shell titulo="Mis acciones correctivas">
      <div className="panel">
        <div className="panel-cuerpo filtros-calidad">
          <label className="fila" style={{ gap: 6 }}>
            <input type="checkbox" checked={soloAbiertas} onChange={(e) => setSoloAbiertas(e.target.checked)} />
            Sólo pendientes y en curso
          </label>
          {lista.cargando && lista.datos === null && <Cargando texto="Cargando tus acciones…" />}
          {lista.error !== null && <ErrorCaja error={lista.error} reintentar={lista.recargar} />}
          {lista.datos !== null &&
            (lista.datos.filas.length === 0 ? (
              <Vacio titulo="No tienes acciones correctivas asignadas" />
            ) : (
              <ul className="lista-nc">
                {lista.datos.filas.map((a) => (
                  <TarjetaAccion
                    key={a.id}
                    accion={a}
                    modo={a.estadoNc === 'ABIERTA' || a.estadoNc === 'EN_TRATAMIENTO' ? 'responsable' : 'lectura'}
                    alCambiar={lista.recargar}
                  />
                ))}
              </ul>
            ))}
        </div>
      </div>
    </Shell>
  );
}

// ------------------------------------------------------------ detalle de incidencia

const VERBO_ESTADO: Record<EstadoIncidencia, string> = {
  ABIERTA: 'Reabrir',
  EN_ANALISIS: 'Pasar a análisis',
  RESUELTA: 'Marcar resuelta',
  CERRADA: 'Cerrar',
  CANCELADA: 'Cancelar incidencia',
};

export function PaginaIncidencia(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const estado = useDatos(() => api.get<IncidenciaDetalle>(`/api/incidencias/${id ?? ''}`), [id]);
  if (estado.cargando && estado.datos === null) {
    return <Shell titulo="Incidencia"><Cargando /></Shell>;
  }
  if (estado.error !== null || estado.datos === null) {
    return <Shell titulo="Incidencia"><ErrorCaja error={estado.error} reintentar={estado.recargar} /></Shell>;
  }
  return <DetalleIncidencia inc={estado.datos} recargar={estado.recargar} />;
}

function DetalleIncidencia({ inc, recargar }: { inc: IncidenciaDetalle; recargar: () => void }): ReactNode {
  const { puede } = useSesion();
  const gestiona = puede('incidencias:gestionar');
  const viva = inc.estado !== 'CERRADA' && inc.estado !== 'CANCELADA';
  const [panel, setPanel] = useState<'estado' | 'editar' | 'escalar' | 'vincular' | null>(null);
  const [destino, setDestino] = useState<EstadoIncidencia | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);

  function hecho(texto: string): void {
    setPanel(null);
    setDestino(null);
    setMensaje(texto);
    recargar();
  }

  return (
    <Shell
      titulo={`Incidencia ${inc.codigo}`}
      migas={[{ texto: 'Calidad', a: '/calidad' }, { texto: inc.codigo }]}
      subtitulo={
        <span className="fila" style={{ gap: 8, flexWrap: 'wrap' }}>
          <EstadoIncidenciaEtiqueta estado={inc.estado} />
          <NivelEtiqueta nivel={inc.gravedad} />
          <span className="etiqueta neutra">{inc.visibleCliente ? 'Visible para el cliente' : 'Interna'}</span>
        </span>
      }
    >
      {mensaje !== null && <div className="nota exito" role="status">{mensaje}</div>}
      <div className="panel">
        <div className="panel-cuerpo">
          <p>{inc.descripcion}</p>
          <div className="datos">
            <Dato e="Centro" v={inc.centroNombre} />
            <div className="dato">
              <div className="etiqueta-dato">OT</div>
              <div className="valor-dato">
                {inc.idOt === null ? 'Sin OT (incidencia del centro)' : <Link to={`/ots/${inc.idOt}`}>{inc.otCodigo}</Link>}
                {inc.otEstado !== null && <span className="pequena tenue"> · {etiquetaEstadoOt(inc.otEstado)}</span>}
              </div>
            </div>
            <Dato e="Ubicación" v={inc.ubicacion ?? '—'} />
            <Dato e="Línea" v={inc.lineaDescripcion ?? '—'} />
            <Dato e="Responsable" v={inc.responsableNombre ?? 'Sin asignar'} />
            <Dato e="Fecha objetivo" v={inc.fechaObjetivo === null ? '—' : `${fecha(inc.fechaObjetivo)}${inc.vencida ? ' · VENCIDA' : ''}`} />
            <Dato e="Reportada por" v={`${inc.creadoPorNombre ?? '—'} · ${fechaHora(inc.creadoEn)}`} />
            {inc.resolucion !== null && (
              <div className="dato">
                <div className="etiqueta-dato">Resolución</div>
                <div className="valor-dato">
                  {inc.resolucion}
                  {inc.resueltaPorNc != null && (
                    <> <Link to={`/calidad/no-conformidades/${inc.resueltaPorNc.id}`}>Ver {inc.resueltaPorNc.codigo}</Link></>
                  )}
                </div>
              </div>
            )}
            {inc.motivoCancelacion !== null && <Dato e="Motivo de cancelación" v={inc.motivoCancelacion} />}
            <div className="dato">
              <div className="etiqueta-dato">No conformidad</div>
              <div className="valor-dato">
                {inc.idNc === null ? (
                  '—'
                ) : (
                  <>
                    <Link to={`/calidad/no-conformidades/${inc.idNc}`}>{inc.codigoNc}</Link>
                    {inc.estadoNc !== null && <> <EstadoNc estado={inc.estadoNc} /></>}
                    <span className="pequena tenue"> · {inc.origenVinculo === 'ESCALADA' ? 'escalada desde aquí' : 'vinculada'}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {gestiona && viva && panel === null && (
            <div className="lista-checks-horizontal sep">
              {inc.idNc === null && puede('nc:crear') && (
                <button type="button" className="principal" onClick={() => setPanel('escalar')}>
                  Escalar a no conformidad
                </button>
              )}
              {inc.idNc === null && puede('nc:tratar') && (
                <button type="button" className="pequeno" onClick={() => setPanel('vincular')}>
                  Vincular a NC existente
                </button>
              )}
              {SIGUIENTES_INCIDENCIA[inc.estado].map((s) => (
                <button key={s} type="button" className="pequeno" onClick={() => { setDestino(s); setPanel('estado'); }}>
                  {VERBO_ESTADO[s]}
                </button>
              ))}
              <button type="button" className="pequeno" onClick={() => setPanel('editar')}>
                Editar
              </button>
            </div>
          )}
          {panel === 'estado' && destino !== null && (
            <FormularioEstadoIncidencia inc={inc} destino={destino} cancelar={() => setPanel(null)} alHacer={hecho} />
          )}
          {panel === 'editar' && <FormularioEditarIncidencia inc={inc} cancelar={() => setPanel(null)} alHacer={hecho} />}
          {panel === 'escalar' && <FormularioEscalar inc={inc} cancelar={() => setPanel(null)} alHacer={hecho} />}
          {panel === 'vincular' && <FormularioVincular inc={inc} cancelar={() => setPanel(null)} alHacer={hecho} />}
        </div>
      </div>

      <div className="panel">
        <div className="panel-cuerpo">
          <Evidencias ambito="INCIDENCIA" ruta={`/api/incidencias/${inc.id}/evidencias`} titulo="Evidencias de la incidencia" />
        </div>
      </div>

      <div className="panel">
        <div className="panel-cabecera"><h2>Línea de tiempo</h2></div>
        <div className="panel-cuerpo">
          <LineaDeTiempo eventos={inc.lineaDeTiempo} sujeto="INCIDENCIA" />
        </div>
      </div>
    </Shell>
  );
}

function Dato({ e, v }: { e: string; v: string }): ReactNode {
  return (
    <div className="dato">
      <div className="etiqueta-dato">{e}</div>
      <div className="valor-dato">{v}</div>
    </div>
  );
}

function useEnvio(): { enviando: boolean; error: unknown; ejecutar: (f: () => Promise<void>) => Promise<void> } {
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  async function ejecutar(f: () => Promise<void>): Promise<void> {
    setEnviando(true);
    setError(null);
    try {
      await f();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }
  return { enviando, error, ejecutar };
}

function FormularioEstadoIncidencia({
  inc,
  destino,
  cancelar,
  alHacer,
}: {
  inc: IncidenciaDetalle;
  destino: EstadoIncidencia;
  cancelar: () => void;
  alHacer: (m: string) => void;
}): ReactNode {
  const idBase = useId();
  const [texto, setTexto] = useState('');
  const { enviando, error, ejecutar } = useEnvio();
  const pide = destino === 'RESUELTA' ? 'Cómo se ha resuelto' : destino === 'CANCELADA' ? 'Motivo de la cancelación' : null;
  return (
    <div className="panel accion-linea sep">
      <div className="panel-cuerpo">
        {error !== null && <ErrorCaja error={error} />}
        {pide !== null && (
          <div className="campo">
            <label htmlFor={`${idBase}-t`}>{pide}</label>
            <textarea id={`${idBase}-t`} rows={2} value={texto} onChange={(e) => setTexto(e.target.value)} />
          </div>
        )}
        <div className="lista-checks-horizontal">
          <button
            type="button"
            className="principal"
            disabled={enviando}
            onClick={() =>
              void ejecutar(async () => {
                await api.post(`/api/incidencias/${inc.id}/estado`, {
                  estado: destino,
                  ...(destino === 'RESUELTA' ? { resolucion: texto.trim() } : {}),
                  ...(destino === 'CANCELADA' ? { motivo: texto.trim() } : {}),
                });
                alHacer(`Incidencia ${inc.codigo}: ${etiquetaEstadoIncidencia(destino).toLowerCase()}.`);
              })
            }
          >
            {enviando ? 'Guardando…' : VERBO_ESTADO[destino]}
          </button>
          <button type="button" className="pequeno" onClick={cancelar} disabled={enviando}>Volver</button>
        </div>
      </div>
    </div>
  );
}

function FormularioEditarIncidencia({
  inc,
  cancelar,
  alHacer,
}: {
  inc: IncidenciaDetalle;
  cancelar: () => void;
  alHacer: (m: string) => void;
}): ReactNode {
  const idBase = useId();
  const responsables = useResponsables();
  const [descripcion, setDescripcion] = useState(inc.descripcion);
  const [gravedad, setGravedad] = useState<GravedadNc>(inc.gravedad);
  const [idResponsable, setIdResponsable] = useState(inc.idResponsable ?? '');
  const [fechaObjetivo, setFechaObjetivo] = useState(inc.fechaObjetivo ?? '');
  const [visibleCliente, setVisibleCliente] = useState(inc.visibleCliente);
  const { enviando, error, ejecutar } = useEnvio();

  return (
    <div className="panel accion-linea sep">
      <div className="panel-cuerpo">
        {error !== null && <ErrorCaja error={error} />}
        <div className="campo">
          <label htmlFor={`${idBase}-d`}>Descripción</label>
          <textarea id={`${idBase}-d`} rows={3} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
        </div>
        <div className="fila" style={{ gap: 12, flexWrap: 'wrap' }}>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-n`}>Nivel</label>
            <select id={`${idBase}-n`} value={gravedad} onChange={(e) => setGravedad(e.target.value as GravedadNc)}>
              <option value="LEVE">Leve</option>
              <option value="GRAVE">Grave</option>
              <option value="CRITICA">Crítica</option>
            </select>
          </div>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-r`}>Responsable</label>
            <SelectorResponsable id={`${idBase}-r`} valor={idResponsable} onChange={setIdResponsable} responsables={responsables} />
          </div>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-f`}>Fecha objetivo</label>
            <input id={`${idBase}-f`} type="date" value={fechaObjetivo} onChange={(e) => setFechaObjetivo(e.target.value)} />
          </div>
        </div>
        <label className="fila" style={{ gap: 6 }}>
          <input type="checkbox" checked={visibleCliente} onChange={(e) => setVisibleCliente(e.target.checked)} />
          Visible para el cliente
        </label>
        <div className="lista-checks-horizontal sep">
          <button
            type="button"
            className="principal"
            disabled={enviando}
            onClick={() =>
              void ejecutar(async () => {
                await api.patch(`/api/incidencias/${inc.id}`, {
                  descripcion: descripcion.trim(),
                  gravedad,
                  idResponsable: idResponsable === '' ? null : idResponsable,
                  fechaObjetivo: fechaObjetivo === '' ? null : fechaObjetivo,
                  visibleCliente,
                });
                alHacer(`Incidencia ${inc.codigo} actualizada.`);
              })
            }
          >
            {enviando ? 'Guardando…' : 'Guardar'}
          </button>
          <button type="button" className="pequeno" onClick={cancelar} disabled={enviando}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

/** Prerrellenado desde la incidencia: una sola decisión, sin volver a escribir nada. */
function FormularioEscalar({
  inc,
  cancelar,
  alHacer,
}: {
  inc: IncidenciaDetalle;
  cancelar: () => void;
  alHacer: (m: string) => void;
}): ReactNode {
  const idBase = useId();
  const navegar = useNavigate();
  const responsables = useResponsables();
  const [descripcion, setDescripcion] = useState(`${inc.codigo}: ${inc.descripcion}`);
  const [gravedad, setGravedad] = useState<GravedadNc>(inc.gravedad);
  const [idResponsable, setIdResponsable] = useState(inc.idResponsable ?? '');
  const [fechaObjetivo, setFechaObjetivo] = useState(inc.fechaObjetivo ?? '');
  const [visibleCliente, setVisibleCliente] = useState(inc.visibleCliente);
  const { enviando, error, ejecutar } = useEnvio();
  const otCerrada = inc.otEstado === 'CERRADA' || inc.otEstado === 'VERIFICADA';  // estado EFECTIVO (servidor)

  return (
    <div className="panel accion-linea sep">
      <div className="panel-cabecera"><h3>Escalar a no conformidad</h3></div>
      <div className="panel-cuerpo">
        {error !== null && <ErrorCaja error={error} />}
        {inc.idOt === null && (
          <div className="nota info" role="note">
            Incidencia general del centro: se creará una no conformidad del centro, sin OT. No afecta a ninguna OT.
          </div>
        )}
        {otCerrada && (
          <div className="nota aviso" role="note">
            La OT {inc.otCodigo} está {inc.otEstado?.toLowerCase()}: al abrir la no conformidad se reabrirá
            automáticamente, como con cualquier otra NC.
          </div>
        )}
        <div className="campo">
          <label htmlFor={`${idBase}-d`}>Descripción de la NC</label>
          <textarea id={`${idBase}-d`} rows={3} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
        </div>
        <div className="fila" style={{ gap: 12, flexWrap: 'wrap' }}>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-n`}>Nivel</label>
            <select id={`${idBase}-n`} value={gravedad} onChange={(e) => setGravedad(e.target.value as GravedadNc)}>
              <option value="LEVE">Leve</option>
              <option value="GRAVE">Grave</option>
              <option value="CRITICA">Crítica</option>
            </select>
          </div>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-r`}>Responsable</label>
            <SelectorResponsable id={`${idBase}-r`} valor={idResponsable} onChange={setIdResponsable} responsables={responsables} />
          </div>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-f`}>Fecha objetivo</label>
            <input id={`${idBase}-f`} type="date" value={fechaObjetivo} onChange={(e) => setFechaObjetivo(e.target.value)} />
          </div>
        </div>
        <label className="fila" style={{ gap: 6 }}>
          <input type="checkbox" checked={visibleCliente} onChange={(e) => setVisibleCliente(e.target.checked)} />
          Visible para el cliente
        </label>
        <div className="lista-checks-horizontal sep">
          <button
            type="button"
            className="principal"
            disabled={enviando}
            onClick={() =>
              void ejecutar(async () => {
                const r = await api.post<ResultadoEscalar>(`/api/incidencias/${inc.id}/escalar`, {
                  descripcion: descripcion.trim(),
                  gravedad,
                  ...(idResponsable !== '' ? { idResponsable } : {}),
                  ...(fechaObjetivo !== '' ? { fechaObjetivo } : {}),
                  visibleCliente,
                });
                alHacer(`No conformidad ${r.codigoNc} creada y vinculada.`);
                navegar(`/calidad/no-conformidades/${r.idNc}`);
              })
            }
          >
            {enviando ? 'Creando…' : 'Crear no conformidad'}
          </button>
          <button type="button" className="pequeno" onClick={cancelar} disabled={enviando}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function FormularioVincular({
  inc,
  cancelar,
  alHacer,
}: {
  inc: IncidenciaDetalle;
  cancelar: () => void;
  alHacer: (m: string) => void;
}): ReactNode {
  const idBase = useId();
  const opciones = useDatos(() => api.get<{ filas: NcVinculable[] }>(`/api/incidencias/${inc.id}/ncs-vinculables`), [inc.id]);
  const [idNc, setIdNc] = useState('');
  const { enviando, error, ejecutar } = useEnvio();
  return (
    <div className="panel accion-linea sep">
      <div className="panel-cuerpo">
        {error !== null && <ErrorCaja error={error} />}
        {opciones.datos !== null && opciones.datos.filas.length === 0 ? (
          <p className="pequena">No hay no conformidades abiertas en este centro.</p>
        ) : (
          <div className="campo">
            <label htmlFor={`${idBase}-nc`}>No conformidad abierta del centro</label>
            <select id={`${idBase}-nc`} value={idNc} onChange={(e) => setIdNc(e.target.value)}>
              <option value="">Elige…</option>
              {(opciones.datos?.filas ?? []).map((n) => (
                <option key={n.id} value={n.id}>{`${n.codigo} · ${n.otCodigo ?? 'sin OT'} · ${n.descripcion.slice(0, 60)}`}</option>
              ))}
            </select>
          </div>
        )}
        <div className="lista-checks-horizontal">
          <button
            type="button"
            className="principal"
            disabled={enviando || idNc === ''}
            onClick={() =>
              void ejecutar(async () => {
                const r = await api.post<{ codigoNc: string }>(`/api/incidencias/${inc.id}/vincular`, { idNc });
                alHacer(`Incidencia vinculada a ${r.codigoNc}.`);
              })
            }
          >
            {enviando ? 'Vinculando…' : 'Vincular'}
          </button>
          <button type="button" className="pequeno" onClick={cancelar} disabled={enviando}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------ detalle de NC

export function PaginaNcCalidad(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const nc = useDatos(() => api.get<NcDetalle>(`/api/no-conformidades/${id ?? ''}`), [id]);
  //  F10.2: abierta desde una línea de OT, la miga vuelve a esa OT (que a su vez conserva su origen).
  const volver = (useLocation().state as { volver?: { a: string; texto: string; state?: unknown } } | null)?.volver ?? null;
  return (
    <Shell
      titulo={nc.datos === null ? 'No conformidad' : `No conformidad ${nc.datos.codigo}`}
      migas={[volver ?? { texto: 'Calidad', a: '/calidad?tab=nc' }, { texto: nc.datos?.codigo ?? '…' }]}
      //  UX-1C: estado junto al título (StatusBadge); gravedad y OT, como metadatos.
      estado={nc.datos === null ? undefined : <EstadoNc estado={nc.datos.estado} />}
      meta={
        nc.datos === null ? undefined : (
          <>
            <NivelEtiqueta nivel={nc.datos.gravedad} />
            {nc.datos.idOt === null
              ? <span className="etiqueta neutra">NC del centro (sin OT)</span>
              : <Link to={`/ots/${nc.datos.idOt}`}>{nc.datos.otCodigo}</Link>}
          </>
        )
      }
    >
      {nc.cargando && nc.datos === null && <Cargando />}
      {nc.error !== null && <ErrorCaja error={nc.error} reintentar={nc.recargar} />}
      {nc.datos !== null && (
        <div className="panel">
          <div className="panel-cuerpo">
            <p>{nc.datos.descripcion}</p>
            <DetalleNc idNc={nc.datos.id} onCambiada={nc.recargar} />
          </div>
        </div>
      )}
    </Shell>
  );
}

// ------------------------------------------------------------ OPERARIO: parte corto

/**
 * Pensado para el móvil y para una mano: dónde (mi OT / mi centro), qué
 * línea si aplica, qué pasa, nivel y una foto. Nada de responsables,
 * fechas ni visibilidad — eso lo decide quien gestiona.
 */
export function PaginaReportarIncidencia(): ReactNode {
  const idBase = useId();
  //  FASE 7.2: sólo lo operativo hoy (en curso/reabiertas, hoy y mañana del
  //  centro) — y el contexto que llega desde la vista offline de una OT.
  const [params] = useSearchParams();
  //  F10.3: llegando desde una tarea de «Mis trabajos», volver a ella (miga y tras enviar).
  const volver = (useLocation().state as { volver?: { a: string; texto: string } } | null)?.volver ?? null;
  const otPedida = params.get('ot');
  const lineaPedida = params.get('linea');
  const misOts = useDatos(() => api.get<{ filas: { id: string; codigo: string; centroDescripcion: string; fechaPlan: string }[] }>('/api/mis-ots?operativas=true'), []);
  const misCentros = useDatos(() => api.get<{ filas: { id: string; descripcion: string }[] }>('/api/mis-centros'), []);
  const misIncidencias = useDatos(() => api.get<PaginaIncidencias>('/api/mis-incidencias?porPagina=20'), []);
  const [donde, setDonde] = useState(otPedida !== null ? `ot:${otPedida}` : '');
  const [fotosAbiertas, setFotosAbiertas] = useState<string | null>(null);
  const detalleOt = useDatos(
    () =>
      donde.startsWith('ot:')
        ? api.get<{ lineas: { id: string; ubicacionCodigo: string; descripcionTarea: string | null; tipoCodigo: string }[] }>(`/api/mis-ots/${donde.slice(3)}`)
        : Promise.resolve(null),
    [donde],
  );
  const [idLinea, setIdLinea] = useState(lineaPedida ?? '');
  const [descripcion, setDescripcion] = useState('');
  const [gravedad, setGravedad] = useState<GravedadNc>('LEVE');
  const [foto, setFoto] = useState<File | null>(null);
  //  Una OT pedida por contexto que ya no está entre las operativas sigue
  //  pudiendo elegirse (el servidor revalida la asignación igualmente).
  const otsVisibles = misOts.datos?.filas ?? [];
  const otFueraDeLista = otPedida !== null && misOts.datos !== null && !otsVisibles.some((o) => o.id === otPedida);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const { enviando, error, ejecutar } = useEnvio();

  function enviar(): Promise<void> {
    return ejecutar(async () => {
      if (donde === '') throw new globalThis.Error('Elige dónde ha pasado.');
      if (descripcion.trim().length < 5) throw new globalThis.Error('Cuenta qué ha pasado (al menos 5 caracteres).');
      const cuerpo = donde.startsWith('ot:')
        ? { idOt: donde.slice(3), ...(idLinea !== '' ? { idOtLinea: idLinea } : {}) }
        : { idCentro: donde.slice(7) };
      const r = await api.post<{ id: string; codigo: string }>('/api/incidencias', { ...cuerpo, descripcion: descripcion.trim(), gravedad });
      let aviso = '';
      if (foto !== null) {
        const datos = new FormData();
        datos.set('contexto', 'GENERAL');
        datos.set('archivo', foto);
        try {
          await api.subir(`/api/incidencias/${r.id}/evidencias`, datos);
        } catch {
          aviso = ' La foto no se ha podido subir: inténtalo de nuevo desde la incidencia o avisa a tu encargado.';
        }
      }
      setMensaje(`Incidencia ${r.codigo} enviada. Gracias.${aviso}`);
      setDescripcion('');
      setIdLinea('');
      setFoto(null);
      misIncidencias.recargar();
    });
  }

  return (
    <Shell titulo="Reportar incidencia" migas={volver === null ? undefined : [volver, { texto: 'Reportar incidencia' }]}>
      {mensaje !== null && (
        <div className="nota exito" role="status">
          {mensaje}
          {volver !== null && <> <Link to={volver.a}>{volver.texto}</Link></>}
        </div>
      )}
      <div className="panel">
        <div className="panel-cuerpo">
          {error !== null && <ErrorCaja error={error} />}
          <div className="campo">
            <label htmlFor={`${idBase}-donde`}>¿Dónde?</label>
            <select id={`${idBase}-donde`} value={donde} onChange={(e) => { setDonde(e.target.value); setIdLinea(''); }}>
              <option value="">Elige tu OT o tu centro…</option>
              {(otsVisibles.length > 0 || otFueraDeLista) && (
                <optgroup label="Mis órdenes de trabajo de hoy">
                  {otFueraDeLista && <option value={`ot:${otPedida}`}>La OT desde la que vienes</option>}
                  {otsVisibles.map((o) => (
                    <option key={o.id} value={`ot:${o.id}`}>{`${o.codigo} · ${o.centroDescripcion} · ${fecha(o.fechaPlan)}`}</option>
                  ))}
                </optgroup>
              )}
              {(misCentros.datos?.filas ?? []).length > 0 && (
                <optgroup label="Mi centro (sin OT)">
                  {(misCentros.datos?.filas ?? []).map((c) => (
                    <option key={c.id} value={`centro:${c.id}`}>{c.descripcion}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
          {detalleOt.datos !== null && detalleOt.datos.lineas.length > 0 && (
            <div className="campo">
              <label htmlFor={`${idBase}-linea`}>Línea (opcional)</label>
              <select id={`${idBase}-linea`} value={idLinea} onChange={(e) => setIdLinea(e.target.value)}>
                <option value="">Toda la OT</option>
                {detalleOt.datos.lineas.map((l) => (
                  <option key={l.id} value={l.id}>{`${l.ubicacionCodigo} · ${l.descripcionTarea ?? l.tipoCodigo}`}</option>
                ))}
              </select>
            </div>
          )}
          <div className="campo">
            <label htmlFor={`${idBase}-desc`}>¿Qué pasa?</label>
            <textarea id={`${idBase}-desc`} rows={4} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
          </div>
          <div className="campo">
            <span className="etiqueta-dato" id={`${idBase}-niv`}>Nivel</span>
            <div className="lista-checks-horizontal" role="radiogroup" aria-labelledby={`${idBase}-niv`}>
              {(['LEVE', 'GRAVE', 'CRITICA'] as const).map((g) => (
                <label key={g} className="fila" style={{ gap: 6 }}>
                  <input type="radio" name={`${idBase}-nivel`} checked={gravedad === g} onChange={() => setGravedad(g)} />
                  {g === 'LEVE' ? 'Leve' : g === 'GRAVE' ? 'Grave' : 'Crítica'}
                </label>
              ))}
            </div>
          </div>
          <div className="campo">
            <label htmlFor={`${idBase}-foto`}>Foto (opcional)</label>
            <input
              id={`${idBase}-foto`}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              onChange={(e) => setFoto(e.target.files?.[0] ?? null)}
            />
          </div>
          <button type="button" className="principal" style={{ width: '100%' }} disabled={enviando} onClick={() => void enviar()}>
            {enviando ? 'Enviando…' : 'Enviar incidencia'}
          </button>
        </div>
      </div>

      <div className="panel">
        <div className="panel-cabecera"><h2>Lo que he reportado</h2></div>
        <div className="panel-cuerpo">
          {misIncidencias.datos !== null &&
            (misIncidencias.datos.filas.length === 0 ? (
              <p className="pequena tenue">Todavía no has reportado ninguna incidencia.</p>
            ) : (
              <ul className="lista-nc">
                {misIncidencias.datos.filas.map((f) => (
                  <li key={f.id} className="nc-tarjeta">
                    <div className="nc-tarjeta-resumen">
                      <div className="fila" style={{ gap: 8, flexWrap: 'wrap' }}>
                        <span className="mono">{f.codigo}</span>
                        <EstadoIncidenciaEtiqueta estado={f.estado} />
                        <span className="pequena tenue">{fechaHora(f.creadoEn)}</span>
                      </div>
                      <p>{f.descripcion}</p>
                      <button
                        type="button"
                        className="pequeno"
                        aria-expanded={fotosAbiertas === f.id}
                        onClick={() => setFotosAbiertas((v) => (v === f.id ? null : f.id))}
                      >
                        {fotosAbiertas === f.id ? 'Ocultar mis fotos' : 'Ver mis fotos'}
                      </button>
                      {fotosAbiertas === f.id && <EvidenciasPropias ruta={`/api/mis-incidencias/${f.id}/evidencias`} />}
                    </div>
                  </li>
                ))}
              </ul>
            ))}
        </div>
      </div>
    </Shell>
  );
}
